import { baseApi } from './baseApi'
import { CatalogItemDto } from 'ts-back-end/src/business/catalog/dto/catalog-item.dto'

export type CatalogItem = CatalogItemDto

type Consumer = 'lab' | 'dme'

export type CatalogItemFormValues = {
  name: string
  sku: string
  description?: string | null
  purchaseUom?: string | null
  quantityPerPurchaseUom?: number | string | null
  picture?: File | null
}

const buildCatalogFormData = (values: Partial<CatalogItemFormValues>): FormData => {
  const formData = new FormData()
  if (typeof values.name !== 'undefined') {
    formData.append('name', values.name ?? '')
  }
  if (typeof values.sku !== 'undefined') {
    formData.append('sku', values.sku ?? '')
  }
  if (typeof values.description !== 'undefined') {
    formData.append('description', values.description ?? '')
  }
  if (typeof values.purchaseUom !== 'undefined') {
    formData.append('purchaseUom', values.purchaseUom ?? '')
  }
  if (typeof values.quantityPerPurchaseUom !== 'undefined' && values.quantityPerPurchaseUom !== null) {
    formData.append('quantityPerPurchaseUom', String(values.quantityPerPurchaseUom))
  }
  if (values.picture) {
    formData.append('picture', values.picture)
  }
  return formData
}

export const catalogApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCatalogItems: builder.query<CatalogItem[], { by?: Consumer; search?: string } | void>({
      query: (params) => {
        const { by = 'lab', search } = params ?? {}
        return {
          url: `/${by}/catalog`,
          ...(search ? { params: { search } } : {}),
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'CatalogItem' as const, id })),
              { type: 'CatalogItem' as const, id: 'LIST' },
            ]
          : [{ type: 'CatalogItem' as const, id: 'LIST' }],
    }),
    getCatalogItem: builder.query<CatalogItem, { by?: Consumer; id: string }>({
      query: ({ by = 'lab', id }) => ({ url: `/${by}/catalog/${id}` }),
      providesTags: (result, error, { id }) => [{ type: 'CatalogItem', id }],
    }),
    getCatalogItemPicture: builder.query<string, { by?: Consumer; id: string }>({
      queryFn: async ({ by = 'lab', id }, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/${by}/catalog/${id}/picture`,
          responseHandler: async (response) => {
            if (!response.ok) return null
            const blob = await response.blob()
            return URL.createObjectURL(blob)
          },
          cache: 'no-cache',
        })
        if (result.error) return { error: result.error }
        if (!result.data) {
          return { error: { status: 'CUSTOM_ERROR' as const, error: 'Picture not found' } }
        }
        return { data: result.data as string }
      },
      providesTags: (result, error, { id }) => [{ type: 'CatalogItem', id: `picture-${id}` }],
    }),
    createCatalogItem: builder.mutation<CatalogItem, Omit<CatalogItemFormValues, 'picture'>>({
      query: (values) => ({
        method: 'POST',
        url: `/lab/catalog`,
        body: values,
      }),
      invalidatesTags: [{ type: 'CatalogItem', id: 'LIST' }],
    }),
    updateCatalogItem: builder.mutation<
      CatalogItem,
      { id: string } & Partial<Omit<CatalogItemFormValues, 'picture'>>
    >({
      query: ({ id, ...values }) => ({
        method: 'PATCH',
        url: `/lab/catalog/${id}`,
        body: values,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CatalogItem', id },
        { type: 'CatalogItem', id: 'LIST' },
      ],
    }),
    setCatalogItemPicture: builder.mutation<CatalogItem, { id: string; picture: File }>({
      query: ({ id, picture }) => ({
        method: 'POST',
        url: `/lab/catalog/${id}/picture`,
        body: buildCatalogFormData({ picture }),
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CatalogItem', id },
        { type: 'CatalogItem', id: 'LIST' },
      ],
    }),
    deleteCatalogItem: builder.mutation<{ result: string }, { id: string }>({
      query: ({ id }) => ({
        method: 'DELETE',
        url: `/lab/catalog/${id}`,
      }),
      invalidatesTags: [{ type: 'CatalogItem', id: 'LIST' }],
    }),
  }),
})

export const {
  useGetCatalogItemsQuery,
  useGetCatalogItemQuery,
  useGetCatalogItemPictureQuery,
  useCreateCatalogItemMutation,
  useUpdateCatalogItemMutation,
  useSetCatalogItemPictureMutation,
  useDeleteCatalogItemMutation,
} = catalogApi
